import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { OccupancyWindow, useGetGroupOccupancyQuery } from '../../../api/group/occupancyApiSlice';
import type { SlotBookingTarget } from '../../../config/bookingServiceMapping';
import { makeAssetDetailsUrl } from '../../../routes';
import { cn } from '../../../utils/utils';
import { OCCUPANCY_HATCH } from '../components/occupancyHatch';
import { utilizationErrorKind } from '../components/utilizationError';
import { peaksByHour, visibleHours } from '../components/utilizationHours';
import { zoneOfLocation, zonedFormatter } from '../../../utils/stationTime';
import {
    WEEK_SLOT_MINUTES,
    WeekSlot,
    daySlotsByHour,
    flattenSlots,
    isBookable,
    weekRange,
    weekStartFor,
    zonedDateKey,
} from './roomWeek';

// One room's week: a row per day, a column per hour, each cell one slot of the
// occupancy endpoint. Its own query and its own hour columns, so rooms can be
// added to the calendar without one slow or failing room holding up the rest.
//
// The allocation-window layer (ADR-0027, M4/M12): a window is a band over the
// hour cells of its weekday, drawn as an outline in the tenant primary — an
// outline, not a fill, because colour means taken (design-decisions.md) and a
// window holds the time without booking it. A booked cell inside a window stays
// taken. Read-only: staff book inside windows as before. A window paused on a
// holiday (D7) holds nothing that day: dashed instead of solid, and its label
// says why ("pausiert · Schulferien", PO 2026-10-24 Q4 A).

export interface WeekRoom {
    assetId: string;
    groupId: string;
    locationId: string;
    /** #3219: the zone of the room's location — the clock its week is drawn on. */
    timeZone: string;
    name: string;
    subtitle?: string;
    /** Where a slot of this room is booked, or `null` if nowhere. */
    booking: SlotBookingTarget | null;
}

/** A picked cell, while the operator chooses where the booking ends. */
export interface WeekAnchor {
    assetId: string;
    from: string;
}

interface RoomWeekGridProps {
    room: WeekRoom;
    /** `YYYY-MM-DD` of the week's Monday — a calendar date, the same in every
     *  zone; each room turns it into its own Monday 00:00 (`room.timeZone`). */
    weekKey: string;
    now: Date;
    canBook: boolean;
    anchor: WeekAnchor | null;
    /** Slots from the anchor to the hovered cell, when that range is bookable. */
    preview: Set<string>;
    onPick: (room: WeekRoom, slot: WeekSlot, slots: WeekSlot[]) => void;
    onHover: (room: WeekRoom, slot: WeekSlot | null, slots: WeekSlot[]) => void;
}

const HOUR_OPTIONS: Intl.DateTimeFormatOptions = { hour: '2-digit', hour12: false };

/** The windows overlapping a slot, by name (several organisations can share an hour). */
const windowsOf = (slot: WeekSlot | undefined, windows: readonly OccupancyWindow[]): OccupancyWindow[] =>
    slot
        ? windows.filter((w) => Date.parse(w.start) < Date.parse(slot.to) && Date.parse(w.end) > Date.parse(slot.from))
        : [];

/** The outline marking a cell inside an allocation window. */
export const WINDOW_OUTLINE = 'ring-1 ring-inset ring-primary';
/** The outline marking a cell inside a window paused on a holiday: free time. */
export const PAUSED_WINDOW_OUTLINE = 'outline-dashed outline-1 -outline-offset-1 outline-primary';

type WindowMark = 'held' | 'paused' | null;

/** Held wins: a cell under a holding window is the organisation's whatever else pauses. */
const windowMarkOf = (inWindows: readonly OccupancyWindow[]): WindowMark => {
    if (inWindows.length === 0) return null;
    return inWindows.some((w) => !w.paused) ? 'held' : 'paused';
};

const windowOutline = (mark: WindowMark): string | null => {
    if (mark === 'held') return WINDOW_OUTLINE;
    if (mark === 'paused') return PAUSED_WINDOW_OUTLINE;
    return null;
};

const cellClassName = (
    slot: WeekSlot | undefined,
    bookable: boolean,
    clickable: boolean,
    isAnchor: boolean,
    inPreview: boolean,
    mark: WindowMark,
): string => {
    if (!slot) return 'bg-transparent border border-dashed border-foreground/10';
    if (isAnchor) return 'bg-primary/50 ring-2 ring-primary';
    if (inPreview) return 'bg-primary/30';
    if (slot.free <= 0) return 'bg-primary text-text-buttonContrast';
    if (!bookable) return cn('bg-foreground/5 opacity-40', windowOutline(mark));
    return cn('bg-foreground/5', windowOutline(mark), clickable && 'hover:bg-primary/20 cursor-pointer');
};

const RoomWeekGrid: React.FC<RoomWeekGridProps> = ({
    room, weekKey, now, canBook, anchor, preview, onPick, onHover,
}) => {
    const { t, i18n } = useTranslation();
    // #3219: the week is the room's own — Monday 00:00 to Monday 00:00 on its
    // location's clock. That clock is first read from the admin location list
    // (`room.timeZone`); the response names the zone the server actually keyed
    // the days in, and where the two differ (a list without the zone, an older
    // list) the week re-anchors on the response's zone and is asked again —
    // the same pattern as UtilizationSection.
    const [responseZone, setResponseZone] = useState<string | undefined>(undefined);
    const anchorZone = responseZone ?? room.timeZone;
    // (`new Date()` only matters for an unreadable key, which the calendar never passes.)
    const { from, to } = useMemo(
        () => weekRange(weekStartFor(weekKey, new Date(), anchorZone), anchorZone),
        [weekKey, anchorZone],
    );
    // `currentData`, not `data`: on a week change RTK Query keeps serving the
    // PREVIOUS week's `data` until the new one arrives, and every free cell of
    // it is a button — a click there would book the week the operator just
    // left. `currentData` is only ever the response for these arguments.
    const { currentData: data, isError, error } = useGetGroupOccupancyQuery({
        groupId: room.groupId,
        from,
        to,
        slotMinutes: WEEK_SLOT_MINUTES,
    });

    if (data?.timeZone && zoneOfLocation(data.timeZone) !== anchorZone) {
        setResponseZone(zoneOfLocation(data.timeZone));
    }

    const slots = useMemo(() => (data ? flattenSlots(data) : []), [data]);
    const windows = useMemo(() => data?.windows ?? [], [data]);

    // Hours and "today" in the zone the response keyed its days in (the
    // room's location; an older server keyed them in the station default).
    const cellZone = data?.timeZone ? zoneOfLocation(data.timeZone) : anchorZone;
    const rows = useMemo(() => {
        const hourFormatter = zonedFormatter('en-GB', HOUR_OPTIONS, cellZone);
        const hourOf = (iso: string): number => Number(hourFormatter.format(new Date(iso))) % 24;
        return (data?.days ?? []).map((day) => ({
            date: day.date,
            byHour: daySlotsByHour(day, slots, hourOf),
            peaks: peaksByHour(day.cells, hourOf),
        }));
    }, [data, slots, cellZone]);

    const hours = useMemo(() => visibleHours(rows.map((row) => row.peaks)), [rows]);

    const dateLocale = i18n.language.startsWith('de') ? 'de-DE' : 'en-GB';
    const formatDay = (isoDate: string): string => {
        const [year, month, day] = isoDate.split('-').map(Number);
        if (!year || !month || !day) return isoDate;
        return new Date(year, month - 1, day).toLocaleDateString(dateLocale, {
            weekday: 'short',
            day: '2-digit',
            month: '2-digit',
        });
    };

    const todayKey = zonedDateKey(now, cellZone);
    const capacity = data?.capacity ?? 0;
    const clickable = canBook && room.booking !== null;
    const failure = utilizationErrorKind(error);

    const slotLabel = (dayLabel: string, time: string, slot: WeekSlot | undefined, bookable: boolean): string => {
        if (!slot) return t('dashboard.assets_availability.week_view.cell.missing', { day: dayLabel, time });
        if (slot.free <= 0) {
            return slot.reserved
                ? t('dashboard.assets_availability.week_view.cell.reserved', { day: dayLabel, time })
                : t('dashboard.assets_availability.week_view.cell.taken', { day: dayLabel, time });
        }
        if (!bookable) return t('dashboard.assets_availability.week_view.cell.past', { day: dayLabel, time });
        return t('dashboard.assets_availability.week_view.cell.free', { day: dayLabel, time });
    };
    const cellLabel = (
        dayLabel: string, hour: number, slot: WeekSlot | undefined, bookable: boolean, inWindows: OccupancyWindow[],
    ): string => {
        const label = slotLabel(dayLabel, `${String(hour).padStart(2, '0')}:00`, slot, bookable);
        if (inWindows.length === 0) return label;
        const namesOf = (list: OccupancyWindow[]) => Array.from(
            new Set(list.map((w) => w.organizationName?.trim() || t('common.not_applicable'))),
        ).join(', ');
        const held = inWindows.filter((w) => !w.paused);
        const paused = inWindows.filter((w) => w.paused);
        const parts = [label];
        if (held.length > 0) {
            parts.push(t('dashboard.assets_availability.week_view.cell.window', { names: namesOf(held) }));
        }
        if (paused.length > 0) {
            // Public wins, as in the expansion: a date can be both.
            const key = paused.some((w) => w.paused === 'public') ? 'window_paused_public' : 'window_paused_school';
            parts.push(t(`dashboard.assets_availability.week_view.cell.${key}`, { names: namesOf(paused) }));
        }
        return parts.join(' ');
    };

    return (
        <section className={'border-t border-foreground/10 pt-3'}>
            <div className={'flex flex-wrap items-baseline gap-x-2 mb-2'}>
                <Link
                    to={makeAssetDetailsUrl(room.assetId)}
                    className={'font-semibold text-foreground hover:text-primary hover:underline transition-colors'}
                >
                    {room.name}
                </Link>
                {room.subtitle && (
                    <span className={'text-xs text-foreground/60'}>{room.subtitle}</span>
                )}
                {canBook && room.booking === null && (
                    <span className={'text-xs text-foreground/60'}>
                        {t('dashboard.assets_availability.week_view.no_booking_route')}
                    </span>
                )}
            </div>

            {isError && (
                <div className={'text-sm text-foreground/60'}>
                    <p>{t('dashboard.assets_availability.week_view.error')}</p>
                    <p className={'text-foreground/40'}>
                        {t(`asset_detail.utilization.error_detail.${failure.kind}`, { status: failure.status })}
                    </p>
                </div>
            )}

            {!isError && !data && (
                <p className={'text-sm text-foreground/40 italic'}>{t('common.loading')}</p>
            )}

            {!isError && data && capacity === 0 && (
                <p className={'text-sm text-foreground/60'}>{t('asset_detail.utilization.no_capacity')}</p>
            )}

            {!isError && data && capacity > 0 && (
                <div className={'overflow-x-auto'}>
                    <table className={'w-full text-sm'} aria-label={room.name}>
                        <thead>
                            <tr className={'text-foreground/60 font-medium'}>
                                <th scope={'col'} className={'text-left font-medium pb-2 pr-3 whitespace-nowrap'}>
                                    {t('asset_detail.utilization.day')}
                                </th>
                                {hours.map((hour) => (
                                    <th key={hour} scope={'col'} className={'font-medium pb-2 text-[10px] min-w-[28px]'}>
                                        {hour}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => {
                                const dayLabel = formatDay(row.date);
                                return (
                                    <tr key={row.date} className={'border-t border-foreground/5'}>
                                        <th
                                            scope={'row'}
                                            className={cn(
                                                'py-1 pr-3 whitespace-nowrap text-left font-normal text-foreground/80',
                                                row.date === todayKey && 'font-semibold text-foreground',
                                            )}
                                        >
                                            {dayLabel}
                                        </th>
                                        {hours.map((hour) => {
                                            const slot = row.byHour.get(hour);
                                            const bookable = !!slot && isBookable(slot, now);
                                            const isAnchor = !!slot && anchor?.assetId === room.assetId
                                                && anchor.from === slot.from;
                                            const inPreview = !!slot && preview.has(slot.from)
                                                && anchor?.assetId === room.assetId;
                                            const inWindows = windowsOf(slot, windows);
                                            const label = cellLabel(dayLabel, hour, slot, bookable, inWindows);
                                            const className = cn(
                                                'h-6 w-full rounded-[2px] flex items-center justify-center',
                                                'text-[9px] leading-none tabular-nums',
                                                cellClassName(slot, bookable, clickable, isAnchor, inPreview, windowMarkOf(inWindows)),
                                            );
                                            const style = slot && slot.free <= 0 && slot.reserved
                                                && !isAnchor && !inPreview
                                                ? OCCUPANCY_HATCH
                                                : undefined;
                                            // Counts only where a slot holds more than one
                                            // unit; a room is taken or free.
                                            const content = slot && capacity > 1 && slot.used > 0 ? slot.used : '';
                                            return (
                                                <td key={hour} className={'py-1 px-[1px]'}>
                                                    {clickable && bookable && slot ? (
                                                        <button
                                                            type={'button'}
                                                            className={className}
                                                            style={style}
                                                            title={label}
                                                            aria-label={label}
                                                            aria-pressed={isAnchor}
                                                            onClick={() => onPick(room, slot, slots)}
                                                            onMouseEnter={() => onHover(room, slot, slots)}
                                                            onMouseLeave={() => onHover(room, null, slots)}
                                                            onFocus={() => onHover(room, slot, slots)}
                                                        >
                                                            {content}
                                                        </button>
                                                    ) : (
                                                        <div className={className} style={style} title={label} role={'img'} aria-label={label}>
                                                            {content}
                                                        </div>
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
};

export default RoomWeekGrid;
